/**
 * PublicValidationTokensPanel
 * Genera y gestiona tokens de acceso publico al estado de validacion.
 * URL publica: /public/validation/{token}
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Link, Plus, Copy, Trash, Eye, Globe, Warning } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function isExpired(iso) {
  if (!iso) return false;
  try { return new Date(iso) < new Date(); } catch { return false; }
}

export function PublicValidationTokensPanel({ lang }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ days_valid: 30, description: '', recipient: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/validation/public-tokens`, { withCredentials: true });
      setTokens(data.tokens || []);
    } catch (err) {
      toast.error(errText(err, 'Error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTokens(); }, []);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/validation/public-tokens`, form, { withCredentials: true });
      toast.success(lang === 'es' ? 'Enlace publico generado' : 'Public link generated');
      setCreateOpen(false);
      setForm({ days_valid: 30, description: '', recipient: '' });
      fetchTokens();
    } catch (err) {
      toast.error(errText(err, 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (token) => {
    try {
      await axios.delete(`${API_URL}/api/validation/public-tokens/${token}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Enlace revocado' : 'Link revoked');
      setConfirmDelete(null);
      fetchTokens();
    } catch (err) {
      toast.error(errText(err, 'Error'));
    }
  };

  const publicUrl = (t) => `${window.location.origin}/public/validation/${t}`;

  const copyLink = (t) => {
    const url = publicUrl(t);
    navigator.clipboard.writeText(url);
    toast.success(lang === 'es' ? 'Enlace copiado al portapapeles' : 'Link copied');
  };

  return (
    <div className="space-y-3" data-testid="public-tokens-panel">
      <div className="bg-[#E8F4F0] border border-[#2d6a4f]/30 rounded-sm p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-[#1b4332] font-semibold flex items-center gap-2">
              <Globe size={16} weight="bold" />
              {lang === 'es' ? 'Enlaces publicos de Estado de Validacion' : 'Public Validation Status Links'}
            </p>
            <p className="text-xs text-[#1b4332]/80 mt-1">
              {lang === 'es'
                ? 'Genera un enlace publico (sin login) para que un cliente, sponsor o auditor (AEMPS, FDA) pueda consultar el estado de validacion del sistema y descargar los PDFs regulatorios firmados. Cada acceso queda registrado en audit_logs.'
                : 'Generate a public link (no login required) for clients, sponsors or auditors to view validation status and download signed PDFs. Each access is logged.'}
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#2d6a4f] hover:bg-[#1b4332] text-white rounded-sm gap-1.5 shrink-0"
            data-testid="public-token-new-btn"
          >
            <Plus size={14} />
            {lang === 'es' ? 'Nuevo enlace' : 'New link'}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-stone-500 py-4 text-center">
          {lang === 'es' ? 'Cargando enlaces...' : 'Loading links...'}
        </div>
      )}

      {!loading && tokens.length === 0 && (
        <div className="bg-white border border-dashed border-stone-300 rounded-sm p-6 text-center" data-testid="public-tokens-empty">
          <Link size={28} className="text-stone-300 mx-auto mb-2" />
          <p className="text-sm text-stone-500">
            {lang === 'es' ? 'No hay enlaces publicos generados todavia.' : 'No public links generated yet.'}
          </p>
        </div>
      )}

      {!loading && tokens.map((t) => {
        const expired = isExpired(t.expires_at);
        const revoked = t.revoked;
        const active = !expired && !revoked;
        return (
          <div
            key={t.token}
            data-testid={`public-token-row-${t.token.slice(0, 8)}`}
            className={`bg-white border rounded-sm p-3 flex items-start gap-3 ${active ? 'border-stone-200' : 'border-stone-200 opacity-60'}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-sm font-medium text-stone-900 truncate">{t.description || 'Sin descripcion'}</span>
                {active && <Badge className="bg-emerald-600 text-white text-[10px]">ACTIVO</Badge>}
                {revoked && <Badge variant="outline" className="text-red-600 border-red-300 text-[10px]">REVOCADO</Badge>}
                {expired && !revoked && <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">CADUCADO</Badge>}
              </div>
              {t.recipient && (
                <div className="text-xs text-stone-500 mb-0.5">
                  <span className="font-medium">{lang === 'es' ? 'Destinatario:' : 'Recipient:'}</span> {t.recipient}
                </div>
              )}
              <div className="text-xs text-stone-500 mb-0.5">
                <span className="font-medium">{lang === 'es' ? 'Creado:' : 'Created:'}</span> {fmtDate(t.created_at)} · {t.created_by_email} ·
                <span className="font-medium ml-1">{lang === 'es' ? 'Expira:' : 'Expires:'}</span> {fmtDate(t.expires_at)} ·
                <span className="font-medium ml-1">{lang === 'es' ? 'Accesos:' : 'Accesses:'}</span> {t.access_count || 0}
              </div>
              {active && (
                <div className="mt-1.5 flex items-center gap-1.5 bg-stone-50 border border-stone-200 rounded px-2 py-1">
                  <code className="text-[10px] text-stone-600 truncate flex-1 font-mono">{publicUrl(t.token)}</code>
                </div>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              {active && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 px-2 gap-1"
                    onClick={() => copyLink(t.token)}
                    data-testid={`public-token-copy-${t.token.slice(0, 8)}`}
                    title={lang === 'es' ? 'Copiar enlace' : 'Copy link'}
                  >
                    <Copy size={14} />
                  </Button>
                  <a
                    href={publicUrl(t.token)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-8 px-2 border border-stone-300 rounded text-stone-700 hover:bg-stone-50"
                    data-testid={`public-token-open-${t.token.slice(0, 8)}`}
                    title={lang === 'es' ? 'Abrir' : 'Open'}
                  >
                    <Eye size={14} />
                  </a>
                </>
              )}
              {!revoked && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2 gap-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setConfirmDelete(t)}
                  data-testid={`public-token-revoke-${t.token.slice(0, 8)}`}
                  title={lang === 'es' ? 'Revocar' : 'Revoke'}
                >
                  <Trash size={14} />
                </Button>
              )}
            </div>
          </div>
        );
      })}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="public-token-create-dialog">
          <DialogHeader>
            <DialogTitle>
              {lang === 'es' ? 'Generar nuevo enlace publico' : 'Generate new public link'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="pt-desc">{lang === 'es' ? 'Descripcion / contexto' : 'Description / context'}</Label>
              <Input
                id="pt-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={lang === 'es' ? 'Ej: Auditoria AEMPS Q2 2026' : 'e.g. AEMPS Audit Q2 2026'}
                className="mt-1"
                data-testid="public-token-description"
              />
            </div>
            <div>
              <Label htmlFor="pt-recipient">{lang === 'es' ? 'Destinatario (opcional)' : 'Recipient (optional)'}</Label>
              <Input
                id="pt-recipient"
                value={form.recipient}
                onChange={(e) => setForm({ ...form, recipient: e.target.value })}
                placeholder={lang === 'es' ? 'Nombre o email del receptor' : 'Name or email'}
                className="mt-1"
                data-testid="public-token-recipient"
              />
            </div>
            <div>
              <Label htmlFor="pt-days">{lang === 'es' ? 'Validez (dias)' : 'Validity (days)'}</Label>
              <Input
                id="pt-days"
                type="number"
                min={1}
                max={365}
                value={form.days_valid}
                onChange={(e) => setForm({ ...form, days_valid: parseInt(e.target.value, 10) || 30 })}
                className="mt-1"
                data-testid="public-token-days"
              />
              <p className="text-xs text-stone-500 mt-1">
                {lang === 'es' ? 'Tras esta fecha el enlace dejara de funcionar automaticamente.' : 'Link will stop working automatically after this date.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={submitting}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={submitting}
              className="bg-[#2d6a4f] hover:bg-[#1b4332] text-white"
              data-testid="public-token-create-confirm"
            >
              {submitting
                ? (lang === 'es' ? 'Generando...' : 'Generating...')
                : (lang === 'es' ? 'Generar enlace' : 'Generate link')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm revoke */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-red-600" />
              {lang === 'es' ? 'Revocar enlace publico' : 'Revoke public link'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-stone-700">
              {lang === 'es'
                ? 'El destinatario perdera el acceso inmediatamente. Esta accion no se puede deshacer (puedes generar un nuevo enlace si lo necesitas).'
                : 'The recipient will lose access immediately. This action cannot be undone (you can generate a new link if needed).'}
            </p>
            {confirmDelete && (
              <p className="text-xs text-stone-500 mt-2 bg-stone-50 border border-stone-200 rounded p-2">
                {confirmDelete.description}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={() => handleRevoke(confirmDelete.token)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="public-token-revoke-confirm"
            >
              {lang === 'es' ? 'Revocar' : 'Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
